import {
    ApolloClient,
    ApolloError,
    InMemoryCache,
    HttpLink,
    defaultDataIdFromObject, FieldMergeFunction,
} from "@apollo/client";
import { createUploadLink } from "apollo-upload-client";
import {RestLink} from "apollo-link-rest";
import {createAuthLink} from "~/auth/AuthLink";
import {createErrorLink, modifyResult} from "~/apollo/errorLink";
import {API_URL} from "~/config";


function createApolloClientLink() {
    const uploadLink = createUploadLink();
    const authLink = createAuthLink();
    const errorLink = createErrorLink(); // error link может отрабатывать только запросы != 200
    if (!API_URL) {
        return null;
    }

    return authLink.concat(errorLink).concat(new RestLink({
        uri: API_URL,
        responseTransformer: async response => {
            let data = await response.json();
            if (typeof data == "object" && typeof data?.data == "object") {
                Object.assign(data, data.data);
                delete data.data;
            }
            data = modifyResult(data);
            if (data.Autovisit) {
                data.Autovisit.number = data.Autovisit.ID;
                data.Autovisit.date = data.Autovisit.Date;
                data.Autovisit.status = data.Autovisit.Status_v2;
                data.Autovisit.pin = data.Autovisit.PIN;
                data.Autovisit.cargoInfo = data.Autovisit.CargoInfo;
                data.Autovisit.timeSlotId = data.Autovisit.TimeSlot?.ID;
                data.Autovisit.checkPointName = data.Autovisit.CheckPoint?.Name;
                data.Autovisit.vehicleId = data.Autovisit.Vehicle?.ID;
                data.Autovisit.driverName = data.Autovisit.Driver?.Name;
            }
            // при 200 ответе нужно вручную обрабатывать ошибки, в остальном справляется errorLink
            if (data.errors && Object.keys(data).filter(x => !["code", "description", "message", "errors"].includes(x)).length == 0) {
                // code, description, message, errors
                throw new ApolloError({graphQLErrors: data.errors});
            }

            return data;
        }
    })).concat(createUploadLink({
        uri: API_URL + "graphql/",
    }));
}


function mergeByKeys(key): FieldMergeFunction {
    return function (existing, incoming, options) {
        if (!existing) {
            return incoming;
        }

        return {
            ...incoming,
            [key]: [
                ...existing[key],
                ...incoming[key],
            ]
        }
    }
}


export class PortApolloClient extends ApolloClient<any> {
    constructor(options = {}) {
        super({
            link: createApolloClientLink(),
            ssrMode: false,
            cache: new InMemoryCache({
                dataIdFromObject: object => {
                    switch (object.__typename) {
                        case 'AutoVisit':
                            return `AutoVisit:${object.number}`;
                        default:
                            return defaultDataIdFromObject(object);
                    }
                },
                typePolicies: {
                    Query: {
                        fields: {
                            customsRequirements: {
                                keyArgs: ["screening_type", "date_from", "date_to", "query"],
                                merge: mergeByKeys("customsRequirements@type({\"name\":\"CustomsRequirement\"})")
                            },
                            releaseNotes: {
                                keyArgs: false,
                                merge(existing, incoming) {
                                    if (!existing) {
                                        return incoming;
                                    }
                                    return {
                                        ...incoming,
                                        edges: existing.edges.concat(incoming.edges),
                                    }
                                }
                            },
                            terminalReleases: {
                                keyArgs: ["status", "date_from", "date_to", "query"],
                                merge: mergeByKeys("terminalReleases@type({\"name\":\"TerminalRelease\"})")
                            },
                            terminalReleaseCargos: {
                                keyArgs: ["type", "query", "empty", "exclude_codes"],
                                merge: mergeByKeys("cargos@type({\"name\":\"TerminalReleaseCargo\"})")
                            },
                            cargoHistoryCargos: {
                                keyArgs: ["query", "type", "status", "type", "dateFrom", "dateTo"],
                                merge: mergeByKeys("cargos@type({\"name\":\"CargoHistoryCargo\"})")
                            },
                            autovisits: {
                                keyArgs: ["carrier_id", "status", "date_from", "date_to", "query"],
                                merge: mergeByKeys("autovisits@type({\"name\":\"AutoVisit\"})")
                            },
                            cargos: {
                                keyArgs: ["autovisit_id", "date", "query", "direction", "exclude_ids", "type", "empty"],
                                merge: mergeByKeys("cargos@type({\"name\":\"AutoVisitCargo\"})")
                            },
                            vesselCalls: {
                               keyArgs: ["status", "dateFrom", "dateTo", "query", "offset", "favorite"],
                               merge: mergeByKeys("vesselCalls")
                            },
                            autoShipmentOrders: {
                                keyArgs: ["id", "dateFrom", "dateTo", "query", "status"],
                                merge: mergeByKeys("autoShipmentOrders@type({\"name\":\"AutoShipmentOrder\"})")
                            }
                        }
                    }
                }
            }),
            ...options
        });
    }
}
